import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreateContractInput } from '../../types/contract';
import { ArrowLeft } from 'lucide-react';
import { CURRENT_USER_ID } from '../../data/mockContracts';
import { ProposalPayload } from '../../types/thread';
import { contractService } from '../../services/contractService';
import { userService } from '../../services/userService';
import { ContractForm } from '../../components/Contracts/ContractForm';
import { useToast } from '../../components/UI/Toast';

const CreateContract = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentUserId, setCurrentUserId] = useState<string>('');

    const { showToast, ToastWrapper } = useToast();

    useEffect(() => {
        const fetchUser = async () => {
            const res = await userService.getUserProfile();
            if (res.success && res.data) {
                setCurrentUserId(res.data.userProfile.userId);
            }
        };
        fetchUser();
    }, []);

    // Initial state setup
    const proposal = location.state?.proposal as ProposalPayload | undefined;

    const [formData] = useState<CreateContractInput>({
        client_user_id: '', // Will be filled if from proposal or selected later
        title: proposal ? `Project Proposal: ${proposal.description.substring(0, 20)}...` : '',
        description: proposal?.description || '',
        requirements: '',
        terms: '',
        total_amount: proposal?.budget || 0,
        currency: proposal ? proposal.currency as any : 'CNY',
        estimated_delivery_date: proposal?.deliveryDate || Date.now() + 30 * 24 * 60 * 60 * 1000,
        milestones: proposal?.milestones || [],
    });

    const fromThreadId = location.state?.fromThreadId as string | undefined;

    const handleBack = () => {
        if (fromThreadId) {
            navigate(`/messages/${fromThreadId}`);
        } else {
            navigate('/contracts');
        }
    };

    const handleSuccess = () => {
        showToast('合同创建成功，已发送给客户审核', 'success');
        setIsSubmitting(false);
        setTimeout(() => {
            if (fromThreadId) {
                navigate(`/messages/${fromThreadId}`);
            } else {
                navigate('/contracts?view=creator');
            }
        }, 1500);
    };

    const handleSubmit = async (data: CreateContractInput, isDraft: boolean = false) => {
        setIsSubmitting(true);
        try {
            // Add status to payload if supported by service (assuming types updated)
            const payload = { ...data, status: isDraft ? 'DRAFT' : 'PENDING_CLIENT_APPROVAL' } as any;
            const res = await contractService.createContract(payload);
            if (res.success) {
                showToast(isDraft ? '草稿已保存' : '合同创建成功，已发送给客户审核', 'success');
                setIsSubmitting(false);
                setTimeout(() => {
                    if (fromThreadId) {
                        navigate(`/messages/${fromThreadId}`);
                    } else {
                        navigate('/contracts?view=creator');
                    }
                }, 1500);
            } else {
                showToast(res.error || (isDraft ? '保存草稿失败' : '创建合同失败'), 'error');
                setIsSubmitting(false);
            }
        } catch (error) {
            console.error('Create contract error:', error);
            showToast(isDraft ? '保存草稿失败' : '创建合同失败，请稍后重试', 'error');
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 pt-24 pb-20 px-6">
            <ToastWrapper />
            <div className="max-w-4xl mx-auto">
                <button
                    onClick={handleBack}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="font-medium">{fromThreadId ? '返回沟通详情' : '返回合同列表'}</span>
                </button>

                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                    <div className="border-b border-slate-200 p-8">
                        <h1 className="text-2xl font-bold text-slate-900 mb-2">创建新合同</h1>
                        <p className="text-slate-600">填写合同详情并设置里程碑</p>
                    </div>

                    <div className="p-8">
                        <ContractForm
                            initialData={formData}
                            onSubmit={handleSubmit}
                            onCancel={handleBack}
                            isSubmitting={isSubmitting}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateContract;
